// Lazy-loaded ReactGA reference (avoids pulling react-ga4 into the initial bundle)
let _ReactGA = null;

const getReactGA = () => {
  if (_ReactGA) return _ReactGA;
  return null;
};

const ensureReactGA = async () => {
  if (_ReactGA) return _ReactGA;
  try {
    const mod = await import('react-ga4');
    _ReactGA = mod.default || mod;
  } catch (e) {
    console.warn('Failed to load react-ga4:', e);
    _ReactGA = null;
  }
  return _ReactGA;
};

// Helper to check if GA should be enabled (only for marketing pages, not authenticated)
const isGAEnabled = () => {
  try {
    if (typeof window === 'undefined') return false;
    // Check if authenticated
    const isAuthenticated = !!(
      localStorage.getItem('access_token') ||
      localStorage.getItem('patientData') ||
      localStorage.getItem('clinicData') ||
      localStorage.getItem('userProfile')
    );
    if (isAuthenticated) return false;
    
    // Check if on marketing page
    const path = window.location.pathname.toLowerCase();
    const marketingPaths = [
      '/marketing',
      '/about',
      '/contact',
      '/product',
      '/specialties',
      '/case-studies',
      '/comparison',
      '/roi-calculator',
      '/blog',
      '/faq',
      '/privacy',
      '/terms',
      '/',
      '/login',
      '/register',
      '/patient-registration',
      '/patient-login',
      '/forgot-password',
      '/forgot-username',
    ];
    const isMarketing = marketingPaths.some(marketingPath => 
      path === marketingPath || path.startsWith(marketingPath + '/')
    ) && !path.startsWith('/dashboard') && !path.startsWith('/patient-dashboard');
    
    return isMarketing && !!process.env.REACT_APP_GA_MEASUREMENT_ID;
  } catch (e) {
    return false;
  }
};

// Business metrics tracking
export const trackBusinessMetric = (metric, value, context = {}) => {
  // GA4 is not HIPAA-compliant - only use on marketing pages
  if (!isGAEnabled()) return;
  
  const eventData = {
    category: 'Business Metrics',
    action: metric,
    value: value,
    label: JSON.stringify(context),
  };

  const ga = getReactGA();
  if (ga) { ga.event(eventData); return; }
  ensureReactGA().then(ga => ga && ga.event(eventData));
};

// User engagement tracking
export const trackEngagement = (action, page, duration = null) => {
  // GA4 is not HIPAA-compliant - only use on marketing pages
  if (!isGAEnabled()) return;
  
  const eventData = {
    category: 'User Engagement',
    action: action,
    label: page,
    value: duration,
  };

  const ga = getReactGA();
  if (ga) { ga.event(eventData); return; }
  ensureReactGA().then(ga => ga && ga.event(eventData));
};

// Feature usage tracking
export const trackFeatureUsage = (feature, action, details = {}) => {
  // GA4 is not HIPAA-compliant - only use on marketing pages
  if (!isGAEnabled()) return;
  
  const eventData = {
    category: 'Feature Usage',
    action: `${feature} - ${action}`,
    label: JSON.stringify(details),
  };

  const ga = getReactGA();
  if (ga) { ga.event(eventData); return; }
  ensureReactGA().then(ga => ga && ga.event(eventData));
};

// Conversion tracking
export const trackConversion = (goal, value = null, details = {}) => {
  // GA4 is not HIPAA-compliant - only use on marketing pages
  if (!isGAEnabled()) return;
  
  const eventData = {
    category: 'Conversions',
    action: goal,
    value: value,
    label: JSON.stringify(details),
  };

  const ga = getReactGA();
  if (ga) { ga.event(eventData); return; }
  ensureReactGA().then(ga => ga && ga.event(eventData));
};

// User journey tracking
export const trackUserJourney = (step, details = {}) => {
  // GA4 is not HIPAA-compliant - only use on marketing pages
  if (!isGAEnabled()) return;
  
  const eventData = {
    category: 'User Journey',
    action: step,
    label: JSON.stringify(details),
  };

  const ga = getReactGA();
  if (ga) { ga.event(eventData); return; }
  ensureReactGA().then(ga => ga && ga.event(eventData));
};

// Error tracking for analytics
export const trackAnalyticsError = (error, context = {}) => {
  // GA4 is not HIPAA-compliant - only use on marketing pages
  if (!isGAEnabled()) return;
  
  const eventData = {
    category: 'Analytics Error',
    action: error.message || 'Unknown Error',
    label: JSON.stringify(context),
  };

  const ga = getReactGA();
  if (ga) { ga.event(eventData); return; }
  ensureReactGA().then(ga => ga && ga.event(eventData));
};

// Custom dimensions and metrics
export const setCustomDimension = (dimension, value) => {
  // GA4 is not HIPAA-compliant - only use on marketing pages
  if (!isGAEnabled()) return;
  
  if (window.gtag) {
    window.gtag('config', process.env.REACT_APP_GA_MEASUREMENT_ID, {
      custom_map: {
        [dimension]: value,
      },
    });
  }
};

// Enhanced ecommerce tracking
export const trackEcommerceEvent = (action, product, value = null) => {
  // GA4 is not HIPAA-compliant - only use on marketing pages
  if (!isGAEnabled()) return;
  
  const eventData = {
    category: 'Ecommerce',
    action: action,
    label: product,
    value: value,
  };

  const ga = getReactGA();
  if (ga) { ga.event(eventData); return; }
  ensureReactGA().then(ga => ga && ga.event(eventData));
};

// User properties tracking
export const setUserProperties = (properties) => {
  // GA4 is not HIPAA-compliant - only use on marketing pages
  if (!isGAEnabled()) return;
  
  if (window.gtag) {
    window.gtag('set', 'user_properties', properties);
  }
}; 